import React from 'react';
import type { SVGProps } from 'react';
import {
  FacebookLogo,
  InstagramLogo,
  LinkedinLogo,
  MastodonLogo,
  PinterestLogo,
  RedditLogo,
  ThreadsLogo,
  TiktokLogo,
  XLogo,
} from '@phosphor-icons/react';
import type { FooterSocialIconKey } from '../types/footer-social';
import { FOOTER_SOCIAL_MEDIA_LINKS } from '../utils/footer/footerSocialMediaLinks';

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.625rem',
};

const linkBaseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.5rem',
  borderRadius: '9999px',
  textDecoration: 'none',
  lineHeight: 0,
  transition: 'background-color 0.2s ease, color 0.2s ease',
};

const linkLightStyle: React.CSSProperties = {
  ...linkBaseStyle,
  // Theme token so the icons stay legible in dark mode (inline styles can't use dark: variants).
  color: 'var(--jc-ink-secondary, #3D3529)',
};

/** Bluesky butterfly (Bootstrap Icons path, viewBox 0 0 16 16). */
function BlueskyGlyph(props: SVGProps<SVGSVGElement>) {
  const { className, ...rest } = props;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden
      className={className}
      {...rest}
    >
      <path d="M3.468 1.948C5.303 3.325 7.276 6.118 8 7.616c.725-1.498 2.698-4.29 4.532-5.668C13.855.955 16 .186 16 2.632c0 .489-.28 4.105-.444 4.692-.572 2.04-2.653 2.561-4.504 2.246 3.236.551 4.06 2.375 2.281 4.2-3.376 3.464-4.852-.87-5.23-1.98-.07-.204-.103-.3-.103-.218 0-.081-.033.014-.102.218-.379 1.11-1.855 5.444-5.231 1.98-1.778-1.825-.955-3.65 2.28-4.2-1.85.315-3.932-.205-4.503-2.246C.28 6.737 0 3.12 0 2.632 0 .186 2.145.955 3.468 1.948" />
    </svg>
  );
}

const phosphorSize = 22;

function SocialGlyph({ icon }: { icon: FooterSocialIconKey }) {
  switch (icon) {
    case 'instagram':
      return <InstagramLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'facebook':
      return <FacebookLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'pinterest':
      return <PinterestLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'x':
      return <XLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'linkedin':
      return <LinkedinLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'mastodon':
      return <MastodonLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'threads':
      return <ThreadsLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'tiktok':
      return <TiktokLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'reddit':
      return <RedditLogo size={phosphorSize} weight="regular" aria-hidden />;
    case 'bluesky':
      return <BlueskyGlyph className="w-[22px] h-[22px]" />;
    default: {
      const _exhaustive: never = icon;
      return _exhaustive;
    }
  }
}

export function FooterSocialMediaBar() {
  const linkStyle = linkLightStyle;
  return (
    <div style={containerStyle}>
      {FOOTER_SOCIAL_MEDIA_LINKS.map((item) => (
        <a
          key={item.href}
          href={item.href}
          target="_blank"
          rel={item.icon === 'mastodon' ? 'noopener noreferrer me' : 'noopener noreferrer'}
          style={linkStyle}
          className="hover:bg-[#F2EADF] dark:hover:bg-[#342415]"
          aria-label={item.label}
        >
          <SocialGlyph icon={item.icon} />
        </a>
      ))}
    </div>
  );
}
